import React, { useEffect, useRef, useState } from 'react';
import { adminProductApi } from '../../../../api/adminProductApi';
import { getApiErrorText } from '../../../../utils/apiResponse';

export default function ProductPackFields({ product, onPackSaved, onBarcodeError, table = false }) {
  const [packBarcode, setBarcode] = useState(product.packBarcode ?? '');
  const [packQuantity, setQuantity] = useState(product.packQuantity ?? '');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const barcodeRef = useRef(null);
  const quantityRef = useRef(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setBarcode(product.packBarcode ?? '');
    setQuantity(product.packQuantity ?? '');
  }, [product.id, product.packBarcode, product.packQuantity]);
  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const response = await adminProductApi.patchProductPack(product.id, {
        barcode: product.barcode,
        packBarcode,
        packQuantity,
      });
      if (mounted.current) onPackSaved(product.id, response.data);
    } catch (error) {
      if (mounted.current) onBarcodeError(getApiErrorText(error));
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const changed =
    String(packBarcode).trim() !== String(product.packBarcode ?? '') ||
    String(packQuantity) !== String(product.packQuantity ?? '');
  const clear = () => {
    setBarcode('');
    setQuantity('');
  };
  const preventSubmit = event => {
    if (event.key === 'Enter') event.preventDefault();
  };
  const inputClassName = `form-control form-control-sm barcode-scan-input${
    table ? '' : ' barcode-scan-input--mobile-card'
  }`;
  const barcodeInput = (
    <input
      ref={barcodeRef}
      aria-label='Штрихкод пачки'
      className={inputClassName}
      style={{ fontSize: '0.875rem' }}
      spellCheck={false}
      onClick={event => event.stopPropagation()}
      type='text'
      autoComplete='off'
      maxLength={80}
      value={packBarcode}
      disabled={saving}
      onKeyDown={preventSubmit}
      onChange={event => setBarcode(event.target.value)}
      onDoubleClick={() => {
        if (table && !saving) clear();
      }}
      title={table ? 'Подвійний клік — очистити пачку. Зміни потрібно зберегти.' : undefined}
    />
  );
  const quantityInput = (
    <input
      ref={quantityRef}
      aria-label='Кількість штук у пачці'
      className={inputClassName}
      style={{ fontSize: '0.875rem' }}
      spellCheck={false}
      onClick={event => event.stopPropagation()}
      type='number'
      inputMode='numeric'
      min='2'
      max='2147483647'
      step='1'
      value={packQuantity}
      disabled={saving}
      onKeyDown={preventSubmit}
      onChange={event => setQuantity(event.target.value)}
    />
  );
  const actions = (
    <div className='d-flex align-items-center mt-1'>
      <button
        type='button'
        className='btn btn-sm btn-outline-primary py-0 px-1'
        disabled={saving || !changed}
        onClick={save}
      >
        {saving ? 'Збереження…' : 'Зберегти'}
      </button>
      <button
        type='button'
        className='btn btn-sm btn-link py-0 px-1'
        title='Очистити обидва поля пачки'
        disabled={saving || (!packBarcode && packQuantity === '')}
        onClick={clear}
      >
        Очистити
      </button>
    </div>
  );
  if (table)
    return (
      <>
        <td
          className='text-monospace small p-1 align-middle'
          style={{ width: '10rem', maxWidth: '10rem' }}
          onClick={() => barcodeRef.current?.focus()}
        >
          {barcodeInput}
          {(changed || saving) && actions}
        </td>
        <td
          className='text-monospace small p-1 align-middle'
          style={{ width: '6rem', maxWidth: '6rem' }}
          onClick={() => quantityRef.current?.focus()}
        >
          {quantityInput}
        </td>
      </>
    );
  return (
    <div>
      <div className='d-flex' style={{ gap: '0.5rem' }}>
        <label className='small d-block mb-1 flex-grow-1' style={{ minWidth: 0 }}>
          Штрихкод пачки{barcodeInput}
        </label>
        <label className='small d-block mb-1' style={{ width: '6rem', flexShrink: 0 }}>
          Штук у пачці{quantityInput}
        </label>
      </div>
      {(changed || saving || Boolean(packBarcode) || packQuantity !== '') && actions}
    </div>
  );
}
